import $ from 'jquery';

import Navigation from 'app/Navigation';
import ModalViewController from 'app/src/controllers/components/ModalViewController';
import CCTerminal from 'app/src/data/models/CCTerminal';
import Localize from 'app/src/localize/Localize';
import PaymentAnalytics from 'app/src/payments/PaymentAnalytics';
import PaymentTerminalModalView from 'app/src/views/payments/PaymentTerminalModalView';

const _Buttons = {
  NO: 'No',
  YES: 'Yes',
  NEW_SALE: 'New Sale',
  DECLINED: 'Declined',
  APPROVED: 'Approved',
};

const _NEW_SALE_URL = '/register.php?form_name=new_transaction';

export default class PaymentTerminalModalViewController extends ModalViewController {
  /**
   * Constructor for a payment terminal modal view controller
   *
   * @param {Terminal} terminal   The terminal being transacted
   * @param {object}  options     Options to set on the modal.  Current options include:
   *                                  message // the message to be shown in the modal
   *                                  canCancel // when true, the transaction can be cancelled
   *                                            // from the terminal
   */
  constructor(terminal, options = {}) {
    if (!terminal) {
      throw 'A terminal must be specified when constructing a PaymentTerminalModalViewController';
    }

    options.title = Localize.translate(
      `<var>${terminal.model.get('name') || 'Terminal'}</var> - Transaction in progress.`
    );

    super(
      // delegate view
      new PaymentTerminalModalView(),
      options
    );

    this._terminal = terminal;
    this._options = options;

    // show a warning if the user tries to reload or navigate away from the page
    $(window).on('beforeunload.processing-payment', () =>
      Localize.translate(
        'A transaction is in progress on the Payment Terminal.\n' +
          'Please cancel it before leaving this screen.'
      )
    );
  }

  /**
   * Called to show the view managed by the modal controller
   */
  show() {
    super.show();

    this._modalView.setMessage(
      this._options.message || Localize.translate('Waiting for payment...')
    );
  }

  /**
   * Called when the modal is destroyed.
   */
  destroy() {
    super.destroy();

    $(window).off('.processing-payment');
  }

  /**
   * Event handler for when the cancel button is clicked.
   */
  onChildViewCancelButtonClicked() {
    if (this._options.canCancel) {
      this._modalView.enableCancelTransactionButton(false);
      this._terminal.cancelTransaction().fail(() => {
        this._modalView.enableCancelTransactionButton(true);
      });
    } else {
      this.view.showBackButton(true);
      this._modalView.showCancelDetails(true);
    }
  }

  /**
   * Event handler for when the user choose to force cancellation of a terminal payment
   * when the terminal does not support POS integration.
   */
  onChildViewNotRespondingClicked() {
    this.view.setTitle(
      Localize.translate(
        `The terminal <var>${
          this._terminal.model.get('name') || 'Terminal'
        }</var> is not responding`
      )
    );
    this.view.showBackButton(false);
    this.view.showModalCloseButton(true);

    if (
      this._terminal.model.get('type') === CCTerminal.TerminalType.TRIPOS ||
      this._terminal.model.get('type') === CCTerminal.TerminalType.LIGHTSPEED
    ) {
      this._modalView.showNoReceiptModal();
      this.setButtons([_Buttons.NEW_SALE]);
    } else {
      this._modalView.showNotRespondingModal();
      this.setButtons([_Buttons.NO, _Buttons.YES]);
    }
  }

  /**
   * Event handler for when the back button is clicked in the cancellation screen for terminals
   * that do not support POS integration.
   */
  onModalBackButtonClicked() {
    this.view.showBackButton(false);
    this._modalView.showPaymentInProgress();
    this.setButtons([]);
  }

  /**
   * Event handler for when the close button is clicked in the modal
   */
  onModalCloseClicked() {
    this._modalView.showCancelDetails();

    this.view.showBackButton(true);
    this.view.setTitle(Localize.translate(this._options.title));
    this.view.showModalCloseButton(false);
    this.setButtons([]);
  }

  /**
   * Event handler for when a modal button is clicked
   *
   * @param {string} buttonName   The name (ID) of the button that was clicked
   */
  onChildViewModalFooterButtonClicked(buttonName) {
    switch (buttonName) {
      case _Buttons.NO:
        this._modalView.showNoReceiptModal();
        this.setButtons([_Buttons.NEW_SALE]);
        break;

      case _Buttons.YES:
        this._modalView.showWithReceiptModal();
        this.setButtons([_Buttons.DECLINED, _Buttons.APPROVED]);
        break;

      case _Buttons.DECLINED:
        this._modalView.showWithDeclinedReceiptModal();
        this._modalView.setTransactionID(this._terminal.lastPayment.id);
        this._modalView.setSaleID(this._options.saleID);
        this.setButtons([_Buttons.NEW_SALE]);
        break;

      case _Buttons.APPROVED:
        this._modalView.showWithApprovedReceiptModal();
        this._modalView.setTransactionID(this._terminal.lastPayment.id);
        this._modalView.setSaleID(this._options.saleID);
        this.setButtons([_Buttons.NEW_SALE]);
        break;

      case _Buttons.NEW_SALE:
        PaymentAnalytics.trackPaymentEvent('Forced cancel', this._options.saleID, {
          terminalID: this._terminal.model.id,
        });

        // invoke the terminal's cancelTransaction method to clean up
        this._terminal.cancelTransaction();

        this.dismiss();

        Navigation.navigateToLegacyUrl(_NEW_SALE_URL);
        break;

      default:
        console.error('Unknown button clicked');
        break;
    }
  }
}
